// REVIEW: If this still works.
import numeric from "numeric";
const ctx = self;
/**
 *
 * @class BundelWorker
 * @author Benjamin Loesch
 */
class BundelWorker {
    constructor() {
        this.data = [];
        this.approxval = null;
        //settings
        this.thresh = 1e-9;
        this.iterLimit = 100;
        //stats
        this.finalIterations = -1;
        this.convergence = false;
        this.correction = [0, 0, 0];
        this.onprogress = null;
    }
    /**
     * calcApproxValue
     * Wählt das ideale Bildpaar aufgrund des grössten Schnittwinkels
     * Rechnet dann den Näherungswert aufgrund Luhman S. 65 -> Schnittpunkt zweier Geraden
     * @returns {number[]}
     */
    calcApproxValue() {
        // prepare input data for worker
        let X0, datarow, R, x, PP, rr;
        const P = [];
        const r = [];
        for (let i = 0; i < this.data.length; i++) {
            // [X0, Y0, Z0, o, p, k, xs, ys, ck, w, projection]
            datarow = this.data[i];
            //1: determine image pair with best viewing angle
            //calc raumstrahlen
            //transformiere punkt auf Bildebene in 3D Punkt im Raum X=X0+R*x -> 3D-Helmert
            X0 = [datarow[0], datarow[1], datarow[2]];
            R = this.getRotMat((datarow[3] * Math.PI) / 180, (datarow[4] * Math.PI) / 180, (datarow[5] * Math.PI) / 180);
            x = [datarow[6] * 0.001, datarow[7] * 0.001, -datarow[8] * 0.001];
            //P -> Position des Bildpunktes auf der Bildebene umgerechnet in 3D-Modellkoordinaten
            PP = numeric.add(X0, numeric.dot(R, x));
            //Panoramic case, overwrite PP
            if (datarow[10] == 1) {
                const xs_p = (datarow[6] * 0.001) / (-datarow[8] * 0.001); //normalized
                const ys_p = (datarow[7] * 0.001) / (-datarow[8] * 0.001);
                //var zs_p = datarow[8];
                const dd = Math.sqrt(xs_p * xs_p + ys_p * ys_p);
                const Xp = (xs_p / dd) * Math.sin(dd);
                const Yp = (ys_p / dd) * Math.sin(dd);
                const Zp = Math.cos(dd);
                const x_pano = numeric.mul(-datarow[8] * 0.001, [Xp, Yp, Zp]);
                PP = numeric.add(X0, numeric.dot(R, x_pano));
            }
            P.push(PP);
            //r -> Raumrichtung die durch P und Projektionszentrum X0 definiert wird
            rr = numeric.sub(PP, X0);
            r.push(rr);
        }
        //Pro Bild haben wir nun eine Raumrichtung. Welche zwei Bilder bzw. Raumrichtungen bilden den besten Schnittwinkel?
        let best_a = 0;
        let bestidx = [-1, -1];
        for (let i = 0; i < r.length; i++) {
            for (let j = 0; j < r.length; j++) {
                if (i == j) {
                    continue;
                }
                //winkel zwischen zwei vektoren... Papula Formelsammlung S.60
                const ang = Math.acos(numeric.dot(r[i], r[j]) / (numeric.norm2(r[i]) * numeric.norm2(r[j])));
                if (ang > best_a) {
                    bestidx = [i, j];
                    best_a = ang;
                }
            }
        }
        //X01 -> Projektionszentrum Bild 1
        //X02 -> Projektionszentrum Bild 2
        const X01 = [this.data[bestidx[0]][0], this.data[bestidx[0]][1], this.data[bestidx[0]][2]];
        const X02 = [this.data[bestidx[1]][0], this.data[bestidx[1]][1], this.data[bestidx[1]][2]];
        //X1s -> X' ... X2s -> X''
        const X1s = P[bestidx[0]];
        const X2s = P[bestidx[1]];
        //Lösung gemäss Luhmann S.65
        const x1 = X01[0];
        const y1 = X01[1];
        const z1 = X01[2];
        const x2 = X02[0];
        const y2 = X02[1];
        const z2 = X02[2];
        let d = numeric.norm2(numeric.sub(X1s, X01));
        const a1 = (X1s[0] - X01[0]) / d;
        const b1 = (X1s[1] - X01[1]) / d;
        const c1 = (X1s[2] - X01[2]) / d;
        d = numeric.norm2(numeric.sub(X2s, X02));
        const a2 = (X2s[0] - X02[0]) / d;
        const b2 = (X2s[1] - X02[1]) / d;
        const c2 = (X2s[2] - X02[2]) / d;
        const a = numeric.det([
            [a1, b1],
            [a2, b2],
        ]);
        const b = numeric.det([
            [b1, c1],
            [b2, c2],
        ]);
        const c = numeric.det([
            [c1, a1],
            [c2, a2],
        ]);
        const den = numeric.det([
            [a1, b1, c1],
            [a2, b2, c2],
            [a, b, c],
        ]);
        const lambda = numeric.det([
            [x1 - x2, y1 - y2, z1 - z2],
            [a, b, c],
            [a2, b2, c2],
        ]) / den;
        const mue = numeric.det([
            [x1 - x2, y1 - y2, z1 - z2],
            [a, b, c],
            [a1, b1, c1],
        ]) / den;
        const xs1 = x1 + lambda * a1;
        const ys1 = y1 + lambda * b1;
        const zs1 = z1 + lambda * c1;
        const xs2 = x2 + mue * a2;
        const ys2 = y2 + mue * b2;
        const zs2 = z2 + mue * c2;
        const xs = (xs1 + xs2) / 2;
        const ys = (ys1 + ys2) / 2;
        const zs = (zs1 + zs2) / 2;
        const angle = Math.acos(a1 * a2 + b1 * b2 + c1 * c2);
        return [xs, ys, zs];
    }
    /**
     * Bündelblockausgleichung mit festgehaltenen Projektionszentren
     * @param data
     */
    runBuendel(data) {
        // this.data:
        //[X0, Y0, Z0, o, p, k, xs, ys, ck, w, projection]
        this.data = JSON.parse(data);
        //console.log("Approx Value from Depthmap: "+this.approxval);
        if (this.approxval == null) {
            this.approxval = this.calcApproxValue();
            //console.log("Approx Value calculated: "+this.approxval);
        }
        //set first observation as origin
        this.approxval = [
            this.approxval[0] - this.data[0][0],
            this.approxval[1] - this.data[0][1],
            this.approxval[2] - this.data[0][2],
        ];
        let xd = [0, 0, 0];
        let A = [];
        let p = [];
        let l = [];
        let X = this.approxval;
        // eslint-disable-next-line
        for (var adjiter = 0; adjiter < this.iterLimit; adjiter++) {
            A = [];
            p = [];
            l = [];
            X = numeric.add(X, [xd[0], xd[1], xd[2]]);
            for (let i = 0; i < this.data.length; i++) {
                //Berechne Bildkoordinaten
                const X0 = [
                    this.data[i][0] - this.data[0][0],
                    this.data[i][1] - this.data[0][1],
                    this.data[i][2] - this.data[0][2],
                ];
                const R = this.getRotMat((this.data[i][3] * Math.PI) / 180, (this.data[i][4] * Math.PI) / 180, (this.data[i][5] * Math.PI) / 180);
                let x_ber;
                if (this.data[i][10] == 0 || this.data[i][10] == 2 || this.data[i][10] == 3)
                    //perspective
                    x_ber = this.coLinEquation(R, X0, X, this.data[i][8]);
                else
                    x_ber = this.coLinEquationPano(R, X0, X, this.data[i][8]);
                //setze L-Vektor zusammen
                l.push(this.data[i][6] - x_ber[0]);
                l.push(this.data[i][7] - x_ber[1]);
                // Ableitungen für A - Matrix
                let deri;
                if (this.data[i][10] == 0 || this.data[i][10] == 2 || this.data[i][10] == 3)
                    //perspective and multiperspective
                    deri = this.getDeriatives(R, X0, X, this.data[i][8]);
                else
                    deri = this.getDeriativesPano(R, X0, X, this.data[i][8]);
                A.push([deri[0], deri[1], deri[2]]);
                A.push([deri[3], deri[4], deri[5]]);
                //set weight
                p.push(this.data[i][9]);
                p.push(this.data[i][9]);
            }
            //P -> Gewichtungsmatrix
            const P = numeric.diag(p);
            const At_P = numeric.dot(numeric.transpose(A), P);
            const At_P_A = numeric.dot(At_P, A);
            // eslint-disable-next-line
            var N = numeric.inv(At_P_A);
            xd = numeric.dot(numeric.dot(N, At_P), l); //inv(APA)*A'*P*l
            const corr = numeric.norm2(xd);
            const msg = {
                topic: "correction_update",
                value: corr,
            };
            if (this.onprogress !== null) {
                this.onprogress(msg);
            }
            if (corr < this.thresh) {
                break;
            }
        }
        if (adjiter >= this.iterLimit) {
            //no convergence
            this.convergence = false;
            return null;
        }
        //v = Ax - l
        const v = numeric.sub(numeric.dot(A, xd), l);
        //m0 = sqrt(sum(v.^2)/(n-u))
        const m0 = Math.sqrt(numeric.dot(v, v) / (this.data.length * 2 - xd.length));
        const s_xyz = [m0 * Math.sqrt(N[0][0]), m0 * Math.sqrt(N[1][1]), m0 * Math.sqrt(N[2][2])];
        //fill in statistics
        this.convergence = true;
        this.finalIterations = adjiter;
        this.correction = X;
        this.m0 = m0;
        this.s_xyz = s_xyz;
        this.residuals = v;
        return [X[0] + this.data[0][0], X[1] + this.data[0][1], X[2] + this.data[0][2]];
    }
    /**
     *
     * @param o
     * @param p
     * @param k
     * @returns {number[][]}
     */
    getRotMat(o, p, k) {
        return [
            [Math.cos(p) * Math.cos(k), -Math.cos(p) * Math.sin(k), Math.sin(p)],
            [
                Math.cos(o) * Math.sin(k) + Math.sin(o) * Math.sin(p) * Math.cos(k),
                Math.cos(o) * Math.cos(k) - Math.sin(o) * Math.sin(p) * Math.sin(k),
                -Math.sin(o) * Math.cos(p),
            ],
            [
                Math.sin(o) * Math.sin(k) - Math.cos(o) * Math.sin(p) * Math.cos(k),
                Math.sin(o) * Math.cos(k) + Math.cos(o) * Math.sin(p) * Math.sin(k),
                Math.cos(o) * Math.cos(p),
            ],
        ];
    }
    /**
     *
     * @param R
     * @param X0
     * @param X
     * @param ck
     * @returns {number[]}
     */
    getDeriatives(R, X0, X, ck) {
        const z = -ck;
        //Kollinearitätsgleichung Bild
        const kx = R[0][0] * (X[0] - X0[0]) + R[1][0] * (X[1] - X0[1]) + R[2][0] * (X[2] - X0[2]);
        const ky = R[0][1] * (X[0] - X0[0]) + R[1][1] * (X[1] - X0[1]) + R[2][1] * (X[2] - X0[2]);
        const N = R[0][2] * (X[0] - X0[0]) + R[1][2] * (X[1] - X0[1]) + R[2][2] * (X[2] - X0[2]);
        //Ableitungen
        const xs_X = (-z / Math.pow(N, 2)) * (R[0][2] * kx - R[0][0] * N);
        const xs_Y = (-z / Math.pow(N, 2)) * (R[1][2] * kx - R[1][0] * N);
        const xs_Z = (-z / Math.pow(N, 2)) * (R[2][2] * kx - R[2][0] * N);
        const ys_X = (-z / Math.pow(N, 2)) * (R[0][2] * ky - R[0][1] * N);
        const ys_Y = (-z / Math.pow(N, 2)) * (R[1][2] * ky - R[1][1] * N);
        const ys_Z = (-z / Math.pow(N, 2)) * (R[2][2] * ky - R[2][1] * N);
        return [xs_X, xs_Y, xs_Z, ys_X, ys_Y, ys_Z];
    }
    /**
     *
     * @param R
     * @param X0
     * @param X
     * @param ck
     * @returns {number[]}
     */
    getDeriativesPano(R, X0, X, ck) {
        const z = -ck;
        //Kollinearitätsgleichung Bild
        const kx = R[0][0] * (X[0] - X0[0]) + R[1][0] * (X[1] - X0[1]) + R[2][0] * (X[2] - X0[2]);
        const ky = R[0][1] * (X[0] - X0[0]) + R[1][1] * (X[1] - X0[1]) + R[2][1] * (X[2] - X0[2]);
        const N = R[0][2] * (X[0] - X0[0]) + R[1][2] * (X[1] - X0[1]) + R[2][2] * (X[2] - X0[2]);
        //Ableitungen gemäss Lochkamera Modell
        const xs_X = (-z / Math.pow(N, 2)) * (R[0][2] * kx - R[0][0] * N);
        const xs_Y = (-z / Math.pow(N, 2)) * (R[1][2] * kx - R[1][0] * N);
        const xs_Z = (-z / Math.pow(N, 2)) * (R[2][2] * kx - R[2][0] * N);
        const ys_X = (-z / Math.pow(N, 2)) * (R[0][2] * ky - R[0][1] * N);
        const ys_Y = (-z / Math.pow(N, 2)) * (R[1][2] * ky - R[1][1] * N);
        const ys_Z = (-z / Math.pow(N, 2)) * (R[2][2] * ky - R[2][1] * N);
        //-----------------------------------------------------------------------------------
        //dx'/dX & dy'/dX
        let H1x = R[0][0];
        let H1y = R[0][1];
        let H2 = R[0][2];
        let Nx = kx;
        let Ny = ky;
        let D = N;
        let Dsq = N * N; //D-squared
        let c = ck;
        let m = Math.pow(Nx, 2) + Math.pow(Ny, 2);
        let rm = Math.sqrt(m); //root m
        let n = Math.atan(Math.sqrt(m) / D);
        let l = Nx * H1x + Ny * H1y;
        const p_xs_X = (-c * n * H1x) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Nx) / (rm * (m / Dsq + 1)) +
            (c * n * l * Nx) / Math.pow(m, 3 / 2);
        const p_ys_X = (-c * n * H1y) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Ny) / (rm * (m / Dsq + 1)) +
            (c * n * l * Ny) / Math.pow(m, 3 / 2);
        //----------------------------------------------------------------------------------------------
        //dx'/dY & dy'/dY
        H1x = R[1][0];
        H1y = R[1][1];
        H2 = R[1][2];
        Nx = kx;
        Ny = ky;
        D = N;
        Dsq = N * N; //D-squared
        c = ck;
        m = Math.pow(Nx, 2) + Math.pow(Ny, 2);
        rm = Math.sqrt(m); //root m
        n = Math.atan(Math.sqrt(m) / D);
        l = Nx * H1x + Ny * H1y;
        const p_xs_Y = (-c * n * H1x) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Nx) / (rm * (m / Dsq + 1)) +
            (c * n * l * Nx) / Math.pow(m, 3 / 2);
        const p_ys_Y = (-c * n * H1y) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Ny) / (rm * (m / Dsq + 1)) +
            (c * n * l * Ny) / Math.pow(m, 3 / 2);
        //----------------------------------------------------------------------------------------------
        //dx'/dY & dy'/dY
        H1x = R[2][0];
        H1y = R[2][1];
        H2 = R[2][2];
        Nx = kx;
        Ny = ky;
        D = N;
        Dsq = N * N; //D-squared
        c = ck;
        m = Math.pow(Nx, 2) + Math.pow(Ny, 2);
        rm = Math.sqrt(m); //root m
        n = Math.atan(Math.sqrt(m) / D);
        l = Nx * H1x + Ny * H1y;
        const p_xs_Z = (-c * n * H1x) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Nx) / (rm * (m / Dsq + 1)) +
            (c * n * l * Nx) / Math.pow(m, 3 / 2);
        const p_ys_Z = (-c * n * H1y) / rm -
            (c * ((-rm * H2) / Dsq + l / (rm * D)) * Ny) / (rm * (m / Dsq + 1)) +
            (c * n * l * Ny) / Math.pow(m, 3 / 2);
        return [p_xs_X, p_xs_Y, p_xs_Z, p_ys_X, p_ys_Y, p_ys_Z];
    }
    /**
     *
     * @param R
     * @param X0
     * @param X
     * @param ck
     * @returns {number[]}
     */
    coLinEquation(R, X0, X, ck) {
        const kx = R[0][0] * (X[0] - X0[0]) + R[1][0] * (X[1] - X0[1]) + R[2][0] * (X[2] - X0[2]);
        const ky = R[0][1] * (X[0] - X0[0]) + R[1][1] * (X[1] - X0[1]) + R[2][1] * (X[2] - X0[2]);
        const N = R[0][2] * (X[0] - X0[0]) + R[1][2] * (X[1] - X0[1]) + R[2][2] * (X[2] - X0[2]);
        const xsb = (-ck * kx) / N;
        const ysb = (-ck * ky) / N;
        return [xsb, ysb];
    }
    /**
     * @param R
     * @param X0
     * @param X
     * @param ck
     * @returns {number[]}
     */
    coLinEquationPano(R, X0, X, ck) {
        const Nx = R[0][0] * (X[0] - X0[0]) + R[1][0] * (X[1] - X0[1]) + R[2][0] * (X[2] - X0[2]);
        const Ny = R[0][1] * (X[0] - X0[0]) + R[1][1] * (X[1] - X0[1]) + R[2][1] * (X[2] - X0[2]);
        const D = R[0][2] * (X[0] - X0[0]) + R[1][2] * (X[1] - X0[1]) + R[2][2] * (X[2] - X0[2]);
        const d = Math.sqrt(Nx * Nx + Ny * Ny);
        const xsb = ((-ck * Nx) / d) * Math.atan(d / D);
        const ysb = ((-ck * Ny) / d) * Math.atan(d / D);
        return [xsb, ysb];
    }
}
ctx.onmessage = (args) => {
    if (args.data["topic"]) {
        switch (args.data["topic"]) {
            case "run":
                // eslint-disable-next-line
                const worker = new BundelWorker();
                worker.onprogress = (msg) => {
                    ctx.postMessage(msg);
                };
                if (args.data["approxval"]) {
                    worker.approxval = args.data["approxval"];
                }
                else {
                    worker.approxval = null;
                }
                if (args.data["observations"]) {
                    let msg = {};
                    try {
                        const result = worker.runBuendel(args.data["observations"]);
                        //if result is null -> no convergence otherwise result = [x,y,z]
                        msg = {
                            topic: "result",
                            result: result,
                            convergence: worker.convergence,
                            finalIterations: worker.finalIterations,
                            correction: worker.correction,
                            m0: worker.m0,
                            s_xyz: worker.s_xyz,
                            residuals: worker.residuals,
                        };
                    }
                    catch (e) {
                        console.log(e);
                        msg = {
                            topic: "result",
                            result: [0, 0, 0],
                            convergence: false,
                            finalIterations: -1,
                            correction: [0, 0, 0],
                            m0: Infinity,
                            s_xyz: [Infinity, Infinity, Infinity],
                            residuals: [],
                        };
                    }
                    ctx.postMessage(msg);
                }
        }
    }
};
//# sourceMappingURL=bundel.worker.js.map